import {Menu} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {DispatchEntity, dispatchStatus} from "@ots-frontend/ots-data";
import {useCallback, useMemo} from "react";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {useDispatchAction} from "./useDispatchAction";
import {IconCircleX, IconEdit, IconTrash} from "@tabler/icons-react";

export const ALL_DISPATCH_ACTIONS = [
  "EDIT",
  "CANCEL",
  "DELETE",
] as const;

export type DispatchAction = typeof ALL_DISPATCH_ACTIONS[number];

export type DispatchActionMenuDropdownProps = {
  dispatch: DispatchEntity
  actions?: DispatchAction[]
  onActionClick?: (action: DispatchAction) => void
  showEmpty?: boolean
}

export const DispatchActionMenuDropdown = (props: DispatchActionMenuDropdownProps) => {
  const {
    dispatch,
    actions = ALL_DISPATCH_ACTIONS,
    onActionClick,
    showEmpty = true
  } = props

  const {hasAccess} = useAuth()
  const {t} = useTranslation(["common"])

  const visibleActions: Set<DispatchAction> = useMemo(() => {
    const enabledActions = new Set<DispatchAction>()
    const actionsSet = actions ? new Set<DispatchAction>(actions) : new Set<DispatchAction>()

    if (hasAccess("dispatch_edit")) {
      if (actionsSet.has("EDIT")) {
        enabledActions.add("EDIT")
      }
      if (dispatch.status.code !== dispatchStatus.canceled.code && actionsSet.has("CANCEL")) {
        enabledActions.add("CANCEL")
      }
    }

    if (hasAccess("dispatch_delete")) {
      if (actionsSet.has("DELETE")) {
        enabledActions.add("DELETE")
      }
    }

    return enabledActions
  }, [dispatch, actions, hasAccess])

  const {
    onDispatchActionClick
  } = useDispatchAction()

  const _onActionClick = useCallback((action: DispatchAction) => {
    if (onActionClick) {
      onActionClick(action)
    } else {
      onDispatchActionClick(dispatch, action)
    }
  }, [onActionClick, onDispatchActionClick, dispatch])

  return (
    <>
      {(showEmpty && visibleActions.size === 0) &&
        <Menu.Item>
          {t("common.action.none", "No Actions")}
        </Menu.Item>
      }
      {visibleActions.has("EDIT") &&
        <Menu.Item icon={<IconEdit size={14}/>} onClick={() => _onActionClick("EDIT")}>
          {t("common.action.edit", "Edit")}
        </Menu.Item>
      }
      {visibleActions.has("CANCEL") &&
        <>
          {visibleActions.size > 1 && <Menu.Divider/>}
          <Menu.Item
            onClick={() => _onActionClick("CANCEL")}
            icon={<IconCircleX size={14}/>}
            color="red">
            {t("common.action.cancel", "Cancel")}
          </Menu.Item>
        </>
      }
      {visibleActions.has("DELETE") &&
        <>
          {visibleActions.size > 1 && <Menu.Divider/>}
          <Menu.Item
            onClick={() => _onActionClick("DELETE")}
            icon={<IconTrash size={14}/>}
            color="red">
            {t("common.action.delete", "Delete")}
          </Menu.Item>
        </>
      }
    </>
  )
}
